import React from 'react';
import {
    ComposedChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    ReferenceLine
} from 'recharts';

export default function MacroImpactChart({ data }) {
    if (!data || data.length === 0) {
        return <div className="chart-placeholder">No data available</div>;
    }

    // Format data for chart
    // multiplier: GDP impact is %, Unemployment impact is pp
    const chartData = data.map(d => ({
        year: d.year,
        gdp: +(d.gdpImpact * 100).toFixed(2), // Convert to %
        unemployment: +(d.unemploymentImpact).toFixed(2) // Already in pp
    }));

    return (
        <div className="chart-container" style={{ height: '300px' }}>
            <h4 style={{ textAlign: 'center', marginBottom: '10px' }}>
                Estimated Macroeconomic Impacts (Illustrative)
            </h4>
            <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                    data={chartData}
                    margin={{ top: 10, right: 30, left: 0, bottom: 20 }}
                >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--bg-tertiary)" />
                    <XAxis
                        dataKey="year"
                        stroke="var(--text-secondary)"
                        style={{ fontSize: '11px', fontWeight: 600 }}
                        tickLine={false}
                    />

                    {/* Left Axis: GDP Impact (%) */}
                    <YAxis
                        yAxisId="left"
                        label={{ value: 'GDP Change (%)', angle: -90, position: 'insideLeft', style: { fill: 'var(--accent-teal)', fontWeight: 700, fontSize: '11px' } }}
                        stroke="var(--accent-teal)"
                        style={{ fontSize: '11px', fontWeight: 600 }}
                    />

                    {/* Right Axis: Unemployment Impact (pp) */}
                    <YAxis
                        yAxisId="right"
                        orientation="right"
                        label={{ value: 'Unemployment (pp)', angle: 90, position: 'insideRight', style: { fill: 'var(--accent-rose)', fontWeight: 700, fontSize: '11px' } }}
                        stroke="var(--accent-rose)"
                        style={{ fontSize: '11px', fontWeight: 600 }}
                    />

                    <Tooltip
                        contentStyle={{
                            backgroundColor: 'rgba(255, 255, 255, 0.96)',
                            border: '1px solid var(--glass-border)',
                            borderRadius: '12px',
                            color: 'var(--text-primary)',
                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                            fontSize: '13px'
                        }}
                        formatter={(value, name) => {
                            if (name === 'GDP vs Baseline') return [`${value > 0 ? '+' : ''}${value}%`, name];
                            if (name === 'Unemployment Rate') return [`${value > 0 ? '+' : ''}${value} pp`, name];
                            return [value, name];
                        }}
                        labelStyle={{ color: 'var(--text-primary)', fontWeight: '800' }}
                    />
                    <Legend
                        wrapperStyle={{ color: 'var(--text-primary)', fontSize: '12px', fontWeight: 600, paddingTop: '10px' }}
                    />

                    <ReferenceLine y={0} yAxisId="left" stroke="var(--text-muted)" strokeDasharray="3 3" />

                    <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="gdp"
                        name="GDP vs Baseline"
                        stroke="var(--accent-teal)"
                        strokeWidth={4}
                        dot={{ r: 4, strokeWidth: 2, fill: 'white' }}
                        activeDot={{ r: 7, strokeWidth: 0 }}
                    />

                    <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="unemployment"
                        name="Unemployment Rate"
                        stroke="var(--accent-rose)"
                        strokeWidth={4}
                        dot={{ r: 4, strokeWidth: 2, fill: 'white' }}
                        activeDot={{ r: 7, strokeWidth: 0 }}
                    />
                </ComposedChart>
            </ResponsiveContainer>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '15px', fontStyle: 'italic', textAlign: 'center', fontWeight: 500 }}>
                * Projections based on simplified elasticities. Not a general equilibrium forecast.
            </div>
        </div>
    );
}
